import { useState, useEffect, useRef } from 'react';
import { ChevronDown, X } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  nome?: string;
  id?: string;
}

export interface SelectWithSearchProps {
  options: SelectOption[];
  value: SelectOption[];
  onChange: (value: SelectOption[]) => void;
  onSearchChange?: (search: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  emptyMessage?: string;
  single?: boolean;
}

export function SelectWithSearch({
  options,
  value = [],
  onChange,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  placeholder = 'Selecione...',
  disabled = false,
  loading = false,
  className = '',
  emptyMessage = 'Nenhuma opção encontrada',
  single = false,
}: SelectWithSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SelectOption[]>(value || []);

  const onSearchChangeRef = useRef(onSearchChange);
  const isFirstRender = useRef(true);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (onSearchChangeRef.current) {
      const timer = setTimeout(() => {
        if (onSearchChangeRef.current) {
          onSearchChangeRef.current(search);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [search]);

  useEffect(() => {
    if (!value || !value.length) {
      setSelected([]);
      return;
    }

    const normalizedValues = value.map(val => {
      const valueKey = typeof val === 'string' ? val : (val.id || val.value);

      const fullOption = options.find(opt => opt.value === valueKey);

      return fullOption || {
        value: valueKey,
        label: (val as any).label || (val as any).nome || `Item ${String(valueKey).slice(0, 4)}...`
      };
    });

    const currentValues = new Set(selected.map(s => s.value));
    const newValues = new Set(normalizedValues.map(v => v.value));

    if (currentValues.size !== newValues.size ||
        ![...currentValues].every(val => newValues.has(val))) {
      setSelected(normalizedValues);
    }
  }, [value, options]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const filteredOptions = onSearchChange
    ? options.filter(option => !selected.some(selectedOpt => selectedOpt.value === option.value))
    : options.filter(option => {
        const isMatch = option.label.toLowerCase().includes(search.toLowerCase());
        return isMatch;
      });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || loading) return;

    const target = e.currentTarget;
    const isBottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 1;

    if (isBottom) {
      onLoadMore();
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectOption = (option: SelectOption) => {
    if (single) {
      setSelected([option]);
      onChange([option]);
      setIsOpen(false);
      setSearch('');
    } else {
      const isSelected = selected.some(item => item.value === option.value);
      const newValue = isSelected
        ? selected.filter(v => v.value !== option.value)
        : [...selected, option];
      setSelected(newValue);
      onChange(newValue);
    }
  };

  const removeOption = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation();
    if (single) {
      setSelected([]);
      onChange([]);
    } else {
      const newSelected = selected.filter(v => v.value !== optionValue);
      setSelected(newSelected);
      onChange(newSelected);
    }
  };

  const isOptionSelected = (option: SelectOption): boolean => {
    return selected.some(selectedOpt => selectedOpt.value === option.value);
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <div
        className={`flex items-center justify-between w-full p-2 border rounded-md cursor-pointer ${disabled ? 'bg-gray-100 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'} ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-300 dark:border-gray-600'}`}
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
      >
        <div className="flex-1 min-w-0 overflow-hidden">
          {selected.length > 0 ? (
            single ? (
              <span className="text-sm dark:text-gray-200">{selected[0].label}</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selected.map(option => (
                  <span
                    key={option.value}
                    className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded-full"
                  >
                    {option.label}
                    <button
                      type="button"
                      onClick={(e) => removeOption(e, option.value)}
                      className="ml-1 text-blue-500 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-100"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )
          ) : (
            <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <input
              type="text"
              className="w-full p-2 text-sm border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          <div className="max-h-60 overflow-y-auto" onScroll={handleScroll}>
            {filteredOptions.length > 0 ? (
              <ul className="py-1">
                {filteredOptions.map((option) => {
                  const optionKey = option.id ? `${option.id}-${option.value}` : option.value;
                  return (
                    <li
                      key={optionKey}
                      className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${isOptionSelected(option) ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                      onClick={() => handleSelectOption(option)}
                    >
                      <div className="flex items-center">
                        {single ? (
                          <input
                            type="radio"
                            checked={isOptionSelected(option)}
                            onChange={() => handleSelectOption(option)}
                            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-600 dark:bg-gray-700"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={isOptionSelected(option)}
                            onChange={() => handleSelectOption(option)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-600 dark:bg-gray-700 dark:checked:bg-blue-600"
                          />
                        )}
                        <span className="ml-2 dark:text-gray-200">{option.label}</span>
                      </div>
                    </li>
                  );
                })}
                {loading && hasMore && (
                  <li className="flex items-center justify-center p-2 text-xs text-gray-500 dark:text-gray-400">
                    Carregando mais...
                  </li>
                )}
              </ul>
            ) : loading && !onSearchChange ? (
              <div className="flex items-center justify-center p-4 text-gray-500 dark:text-gray-400">
                Carregando...
              </div>
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                {emptyMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
